import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import PageLoader from '../../components/PageLoader';
import api from '../../utils/api';
import AdminLayout from '../../components/admin/AdminLayout';
import AutoApprovalSettingCard from '../../components/admin/AutoApprovalSettingCard';
import { useSidebar } from '../../contexts/SidebarContext';
import {
  Building2,
  Clock,
  Star,
  Flag,
  Folder,
  Tag,
  FolderTree,
  MapPin,
  Users,
  UserX,
  CheckCircle,
  XCircle,
  Sparkles,
  ArrowRight,
  TrendingUp,
  AlertCircle,
  Calendar,
  FileText,
  Droplet,
} from 'lucide-react';

const AdminDashboardContent = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { isCollapsed } = useSidebar();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await api.get('/admin/dashboard');
        setStats(response.data);
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const pendingTotal = (stats?.pendingBusinesses || 0) + (stats?.pendingReviews || 0) + (stats?.pendingReports || 0);
  const needsAttention = pendingTotal > 0;

  return (
    <>
      <SEO title="Admin Dashboard" description="Admin dashboard." noindex />

      <main className={`flex-1 transition-all duration-300 ${isCollapsed ? 'ml-16' : 'ml-64'}`}>
        <div className="container-custom py-6 md:py-10">
          {loading ? (
            <PageLoader message="Loading dashboard..." />
          ) : stats ? (
            <div className="space-y-8">
              {/* Welcome & Date */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-gray-50">Dashboard</h1>
                  <p className="mt-1 text-gray-500 dark:text-gray-400 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                  </p>
                </div>
                {needsAttention && (
                  <Link
                    to="/admin/requests"
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200 font-medium hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors animate-scale-in"
                  >
                    <AlertCircle className="w-5 h-5" />
                    {pendingTotal} item{pendingTotal !== 1 ? 's' : ''} need your attention
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                )}
              </div>

              {/* Auto-Approval Setting Radio Component */}
              <AutoApprovalSettingCard />

              {/* Key Metrics – Hero Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                <Link
                  to="/admin/businesses"
                  className="group card p-5 md:p-6 rounded-xl border-l-4 border-l-primary-500 bg-gradient-to-br from-primary-50/80 to-white dark:from-primary-900/10 dark:to-gray-900 hover:shadow-lg transition-all duration-200 animate-scale-in"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Businesses</p>
                      <p className="mt-1 text-3xl font-bold text-gray-800 dark:text-gray-50">{stats.totalBusinesses}</p>
                      {stats.newBusinessesThisWeek > 0 && (
                        <p className="mt-2 flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400">
                          <TrendingUp className="w-3.5 h-3.5" />
                          +{stats.newBusinessesThisWeek} this week
                        </p>
                      )}
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Building2 className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                    </div>
                  </div>
                  <p className="mt-3 text-sm text-primary-600 dark:text-primary-400 font-medium inline-flex items-center gap-1">
                    Manage <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </p>
                </Link>

                <Link
                  to="/admin/requests"
                  className={`group card p-5 md:p-6 rounded-xl border-l-4 transition-all duration-200 animate-scale-in ${
                    stats.pendingBusinesses > 0
                      ? 'border-l-amber-500 bg-gradient-to-br from-amber-50/80 to-white dark:from-amber-900/10 dark:to-gray-900 hover:shadow-lg'
                      : 'border-l-gray-300 dark:border-l-gray-600'
                  }`}
                  style={{ animationDelay: '50ms' }}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Pending Requests</p>
                      <p className="mt-1 text-3xl font-bold text-gray-800 dark:text-gray-50">{stats.pendingBusinesses}</p>
                    </div>
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110 ${
                      stats.pendingBusinesses > 0 ? 'bg-amber-100 dark:bg-amber-900/40' : 'bg-gray-100 dark:bg-gray-800'
                    }`}>
                      <Clock className={`w-6 h-6 ${stats.pendingBusinesses > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-gray-400'}`} />
                    </div>
                  </div>
                  <p className="mt-3 text-sm text-primary-600 dark:text-primary-400 font-medium inline-flex items-center gap-1">
                    Review <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </p>
                </Link>

                <Link
                  to="/admin/reviews"
                  className={`group card p-5 md:p-6 rounded-xl border-l-4 transition-all duration-200 animate-scale-in ${
                    stats.pendingReviews > 0
                      ? 'border-l-indigo-500 bg-gradient-to-br from-indigo-50/80 to-white dark:from-indigo-900/10 dark:to-gray-900 hover:shadow-lg'
                      : 'border-l-gray-300 dark:border-l-gray-600'
                  }`}
                  style={{ animationDelay: '100ms' }}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Pending Reviews</p>
                      <p className="mt-1 text-3xl font-bold text-gray-800 dark:text-gray-50">{stats.pendingReviews || 0}</p>
                      <p className="mt-1 text-xs text-gray-500">{stats.totalReviews || 0} total reviews</p>
                    </div>
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110 ${
                      stats.pendingReviews > 0 ? 'bg-indigo-100 dark:bg-indigo-900/40' : 'bg-gray-100 dark:bg-gray-800'
                    }`}>
                      <Star className={`w-6 h-6 ${stats.pendingReviews > 0 ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-400'}`} />
                    </div>
                  </div>
                  <p className="mt-3 text-sm text-primary-600 dark:text-primary-400 font-medium inline-flex items-center gap-1">
                    Moderate <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </p>
                </Link>

                <Link
                  to="/admin/reports"
                  className={`group card p-5 md:p-6 rounded-xl border-l-4 transition-all duration-200 animate-scale-in ${
                    stats.pendingReports > 0
                      ? 'border-l-rose-500 bg-gradient-to-br from-rose-50/80 to-white dark:from-rose-900/10 dark:to-gray-900 hover:shadow-lg'
                      : 'border-l-gray-300 dark:border-l-gray-600'
                  }`}
                  style={{ animationDelay: '150ms' }}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Pending Reports</p>
                      <p className="mt-1 text-3xl font-bold text-gray-800 dark:text-gray-50">{stats.pendingReports || 0}</p>
                      <p className="mt-1 text-xs text-gray-500">{stats.totalReports || 0} total reports</p>
                    </div>
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110 ${
                      stats.pendingReports > 0 ? 'bg-rose-100 dark:bg-rose-900/40' : 'bg-gray-100 dark:bg-gray-800'
                    }`}>
                      <Flag className={`w-6 h-6 ${stats.pendingReports > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-gray-400'}`} />
                    </div>
                  </div>
                  <p className="mt-3 text-sm text-primary-600 dark:text-primary-400 font-medium inline-flex items-center gap-1">
                    Resolve <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </p>
                </Link>
              </div>

              {/* Content & Structure + Community – Two columns on large */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Content & Structure */}
                <div className="lg:col-span-2 card p-5 md:p-6 rounded-xl animate-scale-in" style={{ animationDelay: '200ms' }}>
                  <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-50 mb-4 flex items-center gap-2">
                    <Folder className="w-5 h-5 text-primary-500" />
                    Content & Structure
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <Link
                      to="/admin/categories"
                      className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50 hover:bg-primary-50 dark:hover:bg-primary-900/20 border border-transparent hover:border-primary-200 dark:hover:border-primary-800 transition-colors group"
                    >
                      <div className="w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center">
                        <Folder className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-gray-800 dark:text-gray-50">{stats.totalCategories}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Categories</p>
                      </div>
                    </Link>
                    <Link
                      to="/admin/subcategories"
                      className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50 hover:bg-primary-50 dark:hover:bg-primary-900/20 border border-transparent hover:border-primary-200 dark:hover:border-primary-800 transition-colors group"
                    >
                      <div className="w-10 h-10 rounded-lg bg-teal-100 dark:bg-teal-900/40 flex items-center justify-center">
                        <FolderTree className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-gray-800 dark:text-gray-50">{stats.totalSubcategories || 0}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Subcategories</p>
                      </div>
                    </Link>
                    <Link
                      to="/admin/tags"
                      className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50 hover:bg-primary-50 dark:hover:bg-primary-900/20 border border-transparent hover:border-primary-200 dark:hover:border-primary-800 transition-colors group"
                    >
                      <div className="w-10 h-10 rounded-lg bg-violet-100 dark:bg-violet-900/40 flex items-center justify-center">
                        <Tag className="w-5 h-5 text-violet-600 dark:text-violet-400" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-gray-800 dark:text-gray-50">{stats.totalTags || 0}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Tags</p>
                      </div>
                    </Link>
                    <Link
                      to="/admin/cities"
                      className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50 hover:bg-primary-50 dark:hover:bg-primary-900/20 border border-transparent hover:border-primary-200 dark:hover:border-primary-800 transition-colors group"
                    >
                      <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center">
                        <MapPin className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-gray-800 dark:text-gray-50">{stats.totalCities}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Cities</p>
                      </div>
                    </Link>
                  </div>
                </div>

                {/* Community */}
                <div className="card p-5 md:p-6 rounded-xl animate-scale-in" style={{ animationDelay: '250ms' }}>
                  <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-50 mb-4 flex items-center gap-2">
                    <Users className="w-5 h-5 text-primary-500" />
                    Community
                  </h2>
                  <div className="space-y-3">
                    <Link
                      to="/admin/users"
                      className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center">
                          <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                          <p className="font-bold text-gray-800 dark:text-gray-50">{stats.totalUsers || 0}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Total Users</p>
                        </div>
                      </div>
                      <ArrowRight className="w-5 h-5 text-gray-400" />
                    </Link>
                    <Link
                      to="/admin/users?isBlocked=true"
                      className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-rose-100 dark:bg-rose-900/40 flex items-center justify-center">
                          <UserX className="w-5 h-5 text-rose-600 dark:text-rose-400" />
                        </div>
                        <div>
                          <p className="font-bold text-gray-800 dark:text-gray-50">{stats.blockedUsers || 0}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Blocked Users</p>
                        </div>
                      </div>
                      <ArrowRight className="w-5 h-5 text-gray-400" />
                    </Link>
                    <Link
                      to="/admin/blood-donors"
                      className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/40 flex items-center justify-center">
                          <Droplet className="w-5 h-5 text-red-600 dark:text-red-400" />
                        </div>
                        <div>
                          <p className="font-bold text-gray-800 dark:text-gray-50">{stats.totalBloodDonors || 0}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Blood Donors</p>
                        </div>
                      </div>
                      <ArrowRight className="w-5 h-5 text-gray-400" />
                    </Link>
                  </div>
                </div>
              </div>

              {/* Business Overview + Recent Activity */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Business status & featured */}
                <div className="card p-5 md:p-6 rounded-xl animate-scale-in" style={{ animationDelay: '300ms' }}>
                  <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-50 mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-primary-500" />
                    Business Overview
                  </h2>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-800">
                      <span className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                        <CheckCircle className="w-4 h-4 text-green-500" /> Approved
                      </span>
                      <span className="font-bold text-gray-800 dark:text-gray-50">{stats.approvedBusinesses}</span>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-800">
                      <span className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                        <XCircle className="w-4 h-4 text-red-500" /> Rejected
                      </span>
                      <span className="font-bold text-gray-800 dark:text-gray-50">{stats.rejectedBusinesses}</span>
                    </div>
                    <Link
                      to="/admin/businesses"
                      className="flex items-center justify-between py-2 rounded-lg bg-amber-50/50 dark:bg-amber-900/10 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors group"
                    >
                      <span className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                        <Sparkles className="w-4 h-4 text-amber-500" /> Featured
                      </span>
                      <span className="font-bold text-gray-800 dark:text-gray-50">{stats.featuredBusinesses || 0}</span>
                    </Link>
                  </div>
                </div>

                {/* Recent activity */}
                <div className="lg:col-span-2 card p-5 md:p-6 rounded-xl animate-scale-in" style={{ animationDelay: '350ms' }}>
                  <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-50 mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-primary-500" />
                    Recent Listings
                  </h2>
                  {stats.recentBusinesses && stats.recentBusinesses.length > 0 ? (
                    <ul className="divide-y divide-gray-100 dark:divide-gray-800">
                      {stats.recentBusinesses.map((b) => (
                        <li key={b._id}>
                          <Link
                            to="/admin/businesses"
                            className="flex items-center justify-between gap-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 rounded-lg px-2 -mx-2 transition-colors group"
                          >
                            <div className="flex items-center gap-3 min-w-0 flex-1">
                              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                b.status === 'approved' ? 'bg-green-500' : b.status === 'pending' ? 'bg-amber-500' : 'bg-red-500'
                              }`} />
                              <div className="min-w-0">
                                <p className="font-medium text-gray-800 dark:text-gray-50 truncate">{b.name}</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">{b.category?.name || '—'}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0 text-right">
                              <span className="text-xs text-gray-400 whitespace-nowrap">
                                {new Date(b.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                              </span>
                              <ArrowRight className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-gray-500 dark:text-gray-400 py-6 text-center">No recent listings yet.</p>
                  )}
                  <Link
                    to="/admin/businesses"
                    className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                  >
                    View all businesses <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>

              {/* Quick actions – when there are pending items */}
              {needsAttention && (
                <div className="flex flex-wrap gap-3 animate-scale-in" style={{ animationDelay: '400ms' }}>
                  {stats.pendingBusinesses > 0 && (
                    <Link to="/admin/requests" className="btn-primary inline-flex items-center gap-2">
                      <Clock className="w-4 h-4" /> Review {stats.pendingBusinesses} request{stats.pendingBusinesses !== 1 ? 's' : ''}
                    </Link>
                  )}
                  {stats.pendingReviews > 0 && (
                    <Link to="/admin/reviews" className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 inline-flex items-center gap-2 transition-colors">
                      <Star className="w-4 h-4" /> Moderate {stats.pendingReviews} review{stats.pendingReviews !== 1 ? 's' : ''}
                    </Link>
                  )}
                  {stats.pendingReports > 0 && (
                    <Link to="/admin/reports" className="px-4 py-2 rounded-lg bg-rose-600 text-white hover:bg-rose-700 inline-flex items-center gap-2 transition-colors">
                      <Flag className="w-4 h-4" /> Resolve {stats.pendingReports} report{stats.pendingReports !== 1 ? 's' : ''}
                    </Link>
                  )}
                </div>
              )}
            </div>
          ) : null}
        </div>
      </main>
    </>
  );
};

const AdminDashboard = () => (
  <AdminLayout>
    <AdminDashboardContent />
  </AdminLayout>
);

export default AdminDashboard;
